import type { PlanState, SeatAssignment, SeatingGuest } from '@/types/seatingPlanner'

const STORAGE_KEY = 'gala:seating-planner:v1'

export function loadLocalPlan(): PlanState | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || parsed.version !== 1) return null
    const plan = parsed as PlanState
    return {
      ...plan,
      assignments: normalizeAssignments(plan.assignments, plan.guests),
    }
  } catch {
    return null
  }
}

export function saveLocalPlan(state: PlanState): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // localStorage can be full or disabled; ignore silently
  }
}

export function clearLocalPlan(): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}

/**
 * Convert legacy `Record<guestId, tableId>` (string values) into the
 * current `Record<guestId, { tableId, seatIndex }>` shape. Pass-through
 * for already-migrated objects. Unknown shapes are dropped.
 */
export function normalizeAssignments(
  raw: unknown,
  guests: SeatingGuest[] = [],
): Record<string, SeatAssignment> {
  if (!raw || typeof raw !== 'object') return {}

  // First pass: collect already-migrated entries and group legacy strings by table.
  const out: Record<string, SeatAssignment> = {}
  const legacyByTable: Record<string, string[]> = {}
  const guestOrder = new Map(guests.map((guest, index) => [guest.id, index]))

  for (const [guestId, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === 'string') {
      ;(legacyByTable[value] ||= []).push(guestId)
    } else if (
      value &&
      typeof value === 'object' &&
      typeof (value as SeatAssignment).tableId === 'string' &&
      typeof (value as SeatAssignment).seatIndex === 'number'
    ) {
      const v = value as SeatAssignment
      out[guestId] = { tableId: v.tableId, seatIndex: v.seatIndex }
    }
  }

  // Second pass: assign sequential seat indices for legacy guests, skipping
  // any indices already claimed by already-migrated entries on the same table.
  for (const [tableId, guestIds] of Object.entries(legacyByTable)) {
    guestIds.sort((a, b) => (guestOrder.get(a) ?? Number.MAX_SAFE_INTEGER) - (guestOrder.get(b) ?? Number.MAX_SAFE_INTEGER))
    const used = new Set<number>()
    for (const a of Object.values(out)) {
      if (a.tableId === tableId) used.add(a.seatIndex)
    }
    let cursor = 0
    for (const guestId of guestIds) {
      while (used.has(cursor)) cursor++
      out[guestId] = { tableId, seatIndex: cursor }
      used.add(cursor)
      cursor++
    }
  }

  return out
}
